import { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, ShieldCheck, AlertTriangle, UserPlus, Lock, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";
import BackButton from "../components/BackButton";

/**
 * Bug 9/50 — Restituisce solo path relativi interni sicuri (mai open redirect):
 *  - deve iniziare con "/"
 *  - non "//" o "/\" (protocol-relative / windows path)
 *  - non contiene "://"
 * Fallback: "/" (home).
 */
function safeNextPath(next) {
  if (!next || typeof next !== "string") return "/";
  if (!next.startsWith("/")) return "/";
  if (next.startsWith("//") || next.startsWith("/\\")) return "/";
  if (next.includes("://")) return "/";
  return next;
}

export default function Login() {
  const { user, config, refresh } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("password"); // password | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { temp_token }
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [rateLimit, setRateLimit] = useState(null); // { remaining, limit } — solo dopo primo fail
  const [attempted, setAttempted] = useState(false);

  // Bug 9/50 — query params `next` e `reason` per redirect con contesto.
  const queryParams = new URLSearchParams(loc.search);
  const nextParam = queryParams.get("next") || queryParams.get("redirect") || "";
  const reason = queryParams.get("reason") || "";

  if (user) {
    const stateFrom = loc.state?.from;
    return <Navigate to={safeNextPath(nextParam || stateFrom)} replace />;
  }

  const readRateLimitHeaders = (headers) => {
    if (!headers) return;
    const rem = headers["x-ratelimit-remaining"];
    const lim = headers["x-ratelimit-limit"];
    if (rem !== undefined && lim !== undefined) {
      setRateLimit({ remaining: parseInt(rem, 10), limit: parseInt(lim, 10) });
    }
  };

  const doPasswordLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await api.login(email.trim(), password, remember);
      setRateLimit(null); // login riuscito → reset warning
      setAttempted(false);
      if (res.requires_2fa) { setTwoFA({ temp_token: res.temp_token }); return; }
      await refresh();
      // Bug 9/50 — post-login redirect, con anti open-redirect.
      const stateFrom = loc.state?.from;
      const destination = safeNextPath(nextParam || stateFrom);
      if (res.user?.must_change_password) nav("/settings/change-password", { replace: true });
      else nav(destination, { replace: true });
    } catch (err) {
      setAttempted(true);
      readRateLimitHeaders(err?.response?.headers);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Caso speciale: must_reset_password (403)
      if (status === 403 && detail && typeof detail === "object" && detail.error === "password_reset_required") {
        toast.success(detail.message || "Ti abbiamo inviato un'email per reimpostare la password.");
        return;
      }
      toast.error((typeof detail === "string" && detail) || "Email o credenziali non corrette");
    } finally { setBusy(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.loginOtpRequest(email.trim());
      setOtpSent(true);
      toast.success("Se l'email è registrata, riceverai un codice.");
    } catch (err) { toast.error("Errore nell'invio del codice"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.loginOtpVerify(email.trim(), otp);
      await refresh();
      // Bug 9/50 — OTP flow rispetta stesso next del password flow.
      nav(safeNextPath(nextParam || loc.state?.from), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido o scaduto");
    } finally { setBusy(false); }
  };

  const verify2FA = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.twoFAVerify(twoFA.temp_token, totpCode || null, recoveryCode || null);
      await refresh();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice 2FA non valido");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-dvh pxos-gradient-surface px-4 py-6" data-testid="login-page">
      <div className="max-w-md mx-auto">
        <BackButton to="/" testid="login-back-btn" />
        <div className="mt-4 pxos-card p-8">
        <div className="text-center">
          <img
            src={config?.brand_logo_full_url || "/branding/logo-full-1024.png"}
            alt={config?.app_name || "Agenda Push"}
            className="mx-auto h-24 w-24 object-contain rounded-2xl"
            data-testid="login-brand-logo"
          />
          <h1 className="mt-4 font-display text-2xl font-semibold">Accedi a {config?.app_name}</h1>
          <p className="mt-1 text-sm opacity-70">
            {twoFA ? "Inserisci il codice della tua app di autenticazione" :
             mode === "password" ? "Email e password" : "Codice via email"}
          </p>
        </div>

        {/* Bug 6/50 — Se non esiste ancora un owner, prima cosa che l'utente
            deve vedere è la CTA di creazione account. La login "normale"
            resta accessibile sotto (in caso si stia semplicemente atterrando
            sull'URL /login), ma la priorità visiva è chiara. */}
        {config?.is_first_setup && !twoFA && (
          <div
            className="mt-6 rounded-2xl border-2 p-4"
            style={{ borderColor: "var(--pxos-primary-accent)", background: "var(--pxos-primary-light)" }}
            data-testid="login-first-setup-cta"
          >
            <div className="text-xs font-mono uppercase tracking-widest opacity-80" style={{ color: "var(--pxos-text-dark)" }}>
              Prima installazione
            </div>
            <div className="mt-1 font-display text-base font-semibold" style={{ color: "var(--pxos-text-dark)" }}>
              Non c'è ancora un account
            </div>
            <p className="mt-1 text-xs" style={{ color: "var(--pxos-text-dark)", opacity: 0.85 }}>
              Registra il primo account owner per iniziare.
            </p>
            <Link
              to="/register"
              className="mt-3 pxos-btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="login-first-setup-btn"
            >
              <UserPlus size={16} /> Crea primo account
            </Link>
          </div>
        )}

        {/* Bug 9/50 — banner di contesto per redirect da rotta protetta o
            sessione scaduta. Sopprimiamo il banner quando è first-setup
            perché quella CTA è più prioritaria. */}
        {!config?.is_first_setup && !twoFA && reason === "auth_required" && (
          <div
            className="mt-6 rounded-xl border px-4 py-3 flex items-start gap-3"
            style={{ borderColor: "#F5C24A", background: "#FFF8E1", color: "#8A5A00" }}
            role="alert"
            data-testid="login-auth-required-alert"
          >
            <Lock size={18} className="mt-0.5 shrink-0" aria-hidden />
            <div>
              <div className="text-sm font-semibold">Accedi per continuare</div>
              <div className="text-xs opacity-90">
                La pagina richiesta è riservata agli utenti registrati.
              </div>
            </div>
          </div>
        )}
        {!config?.is_first_setup && !twoFA && reason === "session_expired" && (
          <div
            className="mt-6 rounded-xl border px-4 py-3 flex items-start gap-3"
            style={{ borderColor: "#F5C24A", background: "#FFF8E1", color: "#8A5A00" }}
            role="alert"
            data-testid="login-session-expired-alert"
          >
            <Clock size={18} className="mt-0.5 shrink-0" aria-hidden />
            <div>
              <div className="text-sm font-semibold">Sessione scaduta</div>
              <div className="text-xs opacity-90">Accedi di nuovo per riprendere da dove eri.</div>
            </div>
          </div>
        )}

        {twoFA ? (
          <form onSubmit={verify2FA} className="mt-8 space-y-4" data-testid="login-2fa-form">
            <Field label="Codice TOTP" testid="totp-code">
              <input value={totpCode} onChange={(e) => setTotpCode(e.target.value)}
                     className="pxos-input" placeholder="123 456" inputMode="numeric"
                     data-testid="login-totp-input" />
            </Field>
            <div className="text-xs opacity-70 text-center">— oppure —</div>
            <Field label="Codice di recupero" testid="recovery-code">
              <input value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
                     className="pxos-input" placeholder="XXXXX-XXXXX" data-testid="login-recovery-input" />
            </Field>
            <button disabled={busy} className="pxos-btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="login-2fa-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Verifica
            </button>
          </form>
        ) : mode === "password" ? (
          <form onSubmit={doPasswordLogin} className="mt-8 space-y-4" data-testid="login-form">
            <Field label="Email" testid="email">
              <input type="email" required autoComplete="email" autoFocus value={email}
                     onChange={(e) => setEmail(e.target.value)} className="pxos-input"
                     placeholder="nome@dominio.com" data-testid="login-email-input" />
            </Field>
            <Field label="Password" testid="password">
              <input type="password" required autoComplete="current-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} className="pxos-input"
                     placeholder="••••••••••••" data-testid="login-password-input" />
            </Field>
            {attempted && rateLimit && rateLimit.remaining > 0 && rateLimit.remaining < 10 && (
              <div
                className="rounded-lg border px-3 py-2 text-xs font-medium flex items-start gap-2"
                style={{ background: "#FFF8E1", borderColor: "#F5C24A", color: "#8A5A00" }}
                data-testid="login-rate-limit-warning"
                role="alert"
              >
                <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  Attenzione: ti restano <b>{rateLimit.remaining}</b>{" "}
                  tentativ{rateLimit.remaining === 1 ? "o" : "i"} prima del blocco temporaneo.
                </span>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" />
              Ricorda questo dispositivo
            </label>
            <button disabled={busy} className="pxos-btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="login-submit-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Accedi
            </button>
            <div className="text-sm text-center mt-2">
              <Link to="/password-reset" className="font-semibold underline decoration-2 underline-offset-2" style={{ color: "var(--pxos-accent)" }} data-testid="login-forgot-password">
                Password dimenticata?
              </Link>
            </div>
            {/* Bug 6/50 — microcopy anti-confusione: l'utente che non ha ancora
                un account tende a pensare "il reset non funziona / non arrivano
                le email"; qui è esplicito che serve un account esistente. */}
            <p className="text-[11px] text-center opacity-70" data-testid="login-reset-hint">
              Il reset funziona solo per email già registrata e verificata.
              {" "}Non hai ancora un account?{" "}
              <Link to="/register" className="underline font-semibold" data-testid="login-register-link">
                Registrati
              </Link>.
            </p>
            <div className="text-xs text-center opacity-80">
              <button type="button" onClick={() => setMode("otp")} className="underline" data-testid="login-switch-otp">Accedi con un codice via email</button>
            </div>
            <p className="text-[11px] text-center opacity-70" data-testid="login-otp-hint">
              Solo se hai già un account registrato e verificato.
            </p>
          </form>
        ) : (
          <form onSubmit={otpSent ? verifyOtp : requestOtp} className="mt-8 space-y-4" data-testid="login-otp-form">
            <Field label="Email" testid="otp-email">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="pxos-input" placeholder="nome@dominio.com" data-testid="login-otp-email" />
            </Field>
            {otpSent && (
              <Field label="Codice ricevuto" testid="otp-code">
                <input value={otp} onChange={(e) => setOtp(e.target.value)}
                       className="pxos-input" placeholder="123456" inputMode="numeric" data-testid="login-otp-code" />
              </Field>
            )}
            <button disabled={busy} className="pxos-btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="login-otp-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {otpSent ? "Accedi con il codice" : "Invia codice via email"}
            </button>
            <div className="text-sm text-center">
              <button type="button" onClick={() => setMode("password")} className="font-semibold underline decoration-2 underline-offset-2" style={{ color: "var(--pxos-accent)" }} data-testid="login-switch-password">
                Torna a email + password
              </button>
            </div>
            <div className="text-xs text-center opacity-80">
              <Link to="/password-reset" className="underline" data-testid="login-otp-forgot">Password dimenticata?</Link>
            </div>
            <p className="text-[11px] text-center opacity-70" data-testid="login-otp-mode-hint">
              L'accesso via codice funziona solo per email già registrata e verificata.
              {" "}Non hai ancora un account?{" "}
              <Link to="/register" className="underline font-semibold" data-testid="login-otp-register-link">Registrati</Link>.
            </p>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--pxos-text-dark)" }} data-testid={`field-label-${testid}`}>{label}</span>
      <span className="mt-1 block">{children}</span>
    </label>
  );
}
